/**
 * Los pasos para poder reservar. UNA definición, para todas las pantallas.
 *
 * POR QUÉ EXISTE. Los mismos seis trámites tenían TRES vocabularios distintos, y
 * la misma persona los leía los tres en el mismo camino:
 *
 *   paso        hub de verificación        checkout                          ficha
 *   ─────────── ────────────────────────── ───────────────────────────────── ─────────────────
 *   DUI         "Documento de identidad"   "Verificá tu DUI o pasaporte"     "Foto de tu DUI"
 *   selfie      "Selfie de verificación"   "Tomate la selfie de …"           "Una selfie"
 *   teléfono    "Teléfono"                 "Confirmá tu número de teléfono"  "Confirmá tu teléfono"
 *
 * Tres nombres para el mismo trámite es tres trámites, para quien lo lee. Y no
 * era un descuido de redacción sino de estructura: cada pantalla inventaba su
 * lista porque no había ninguna. La cuarta pantalla iba a inventar la cuarta.
 *
 * EL ORDEN ES PARTE DE LA DEFINICIÓN, y es de lo barato a lo caro. Es el mismo
 * que devuelve `private.booking_eligibility`; si divergen, el checkout enumera
 * en un orden y la ficha en otro. Cuando cambie uno, tiene que cambiar el otro,
 * y `validate-verification-steps.mjs` lo comprueba contra este archivo.
 *
 * QUÉ NO VIVE ACÁ. La decisión de si falta o no: esa es del servidor y de nadie
 * más. Este archivo solo sabe cómo se llama cada paso, cuánto tarda y a dónde
 * lleva. Las tres pantallas preguntan a `booking_eligibility` y pintan con esto.
 */
import type { BookingBlocker } from '../types/verification.types'

export interface VerificationStep {
  /** El código que devuelve el servidor. Es la llave, no la etiqueta. */
  blocker: BookingBlocker
  /** La ÚNICA etiqueta de este paso en todo el producto. */
  labelKey: string
  /**
   * Cuánto tarda de verdad, no cuánto nos gustaría. La gente no abandona por el
   * esfuerzo sino por no saber cuánto es: un número acotado vence a un
   * horizonte abierto.
   */
  etaKey: string
  /** A dónde se va para hacerlo. */
  href: string
}

/**
 * Los pasos que una persona puede COMPLETAR, en el orden en que se le piden.
 *
 * `identity_review_pending`, `account_deletion_requested` y `unknown_profile`
 * quedan fuera a propósito: no son pasos, son estados. Nadie puede "hacer" una
 * revisión pendiente, y ofrecerla como tarea es prometer un control que no
 * existe. Se muestran con su propio texto, en `STATE_BLOCKERS`.
 */
export const VERIFICATION_STEPS: readonly VerificationStep[] = [
  /*
   * OJO CON ESTOS DOS: no viven bajo `/profile/verification/`.
   *
   * Los escribí como `/profile/verification/phone` y `/profile/verification/email`
   * por simetría con los otros cuatro, y ninguna de las dos rutas existe. La del
   * teléfono es el paso que le falta a 1.077 personas —el 87,4% de las cuentas—
   * así que era el enlace muerto más caro posible.
   *
   * Las pantallas de contacto son de `(auth)` porque también se usan durante el
   * registro, antes de que exista un perfil que verificar. `validate-verification-steps`
   * comprueba ahora que cada `href` corresponda a un archivo de ruta real.
   */
  {
    blocker: 'phone',
    labelKey: 'steps.phone.label',
    etaKey: 'steps.phone.eta',
    href: '/(auth)/verify-phone',
  },
  {
    blocker: 'email',
    labelKey: 'steps.email.label',
    etaKey: 'steps.email.eta',
    href: '/(auth)/verify-email',
  },
  {
    blocker: 'identity_document',
    labelKey: 'steps.identity_document.label',
    etaKey: 'steps.identity_document.eta',
    href: '/profile/verification/identity',
  },
  {
    blocker: 'selfie',
    labelKey: 'steps.selfie.label',
    etaKey: 'steps.selfie.eta',
    href: '/profile/verification/selfie',
  },
  {
    blocker: 'driver_license',
    labelKey: 'steps.driver_license.label',
    etaKey: 'steps.driver_license.eta',
    href: '/profile/verification/license',
  },
  {
    blocker: 'aml_declaration',
    labelKey: 'steps.aml_declaration.label',
    etaKey: 'steps.aml_declaration.eta',
    href: '/profile/verification/compliance',
  },
] as const

/** Lo que no es un paso: se explica, no se ofrece como tarea. */
export const STATE_BLOCKERS: readonly BookingBlocker[] = [
  'identity_review_pending',
  'account_deletion_requested',
  'unknown_profile',
] as const

/**
 * Los estados que hacen IRRELEVANTE cualquier lista de pasos.
 *
 * No todos los estados pesan igual, y tratarlos igual produce pantallas
 * absurdas. `identity_review_pending` CONVIVE con pasos --aparece cuando los
 * tres documentos estan aprobados pero todavia falta, por ejemplo, el telefono--
 * asi que la lista sigue siendo util y solo hay que agregar la nota.
 *
 * Estos dos no: a quien pidio borrar su cuenta, o a quien no tiene perfil,
 * ofrecerle "confirma tu telefono" es mandarlo a hacer un tramite que no va a
 * desbloquear nada. Ahi la lista se calla y se explica el estado.
 */
export const TERMINAL_BLOCKERS: readonly BookingBlocker[] = [
  'account_deletion_requested',
  'unknown_profile',
] as const

export function terminalBlockerIn(missing: readonly BookingBlocker[]): BookingBlocker | null {
  return TERMINAL_BLOCKERS.find((b) => missing.includes(b)) ?? null
}

const BY_BLOCKER = new Map(VERIFICATION_STEPS.map((s) => [s.blocker, s]))

export function verificationStepFor(blocker: BookingBlocker): VerificationStep | null {
  return BY_BLOCKER.get(blocker) ?? null
}

export function isStateBlocker(blocker: BookingBlocker): boolean {
  return STATE_BLOCKERS.includes(blocker)
}

/**
 * Lo que falta, ordenado como este archivo dice — no como venga en el arreglo.
 *
 * El servidor ya devuelve en este orden, pero depender de eso ata la pantalla a
 * que nadie toque la función. Ordenar acá cuesta nada y hace que un cambio en el
 * servidor no pueda desordenar la lista en silencio.
 */
export function orderedPendingSteps(missing: readonly BookingBlocker[]): VerificationStep[] {
  return VERIFICATION_STEPS.filter((s) => missing.includes(s.blocker))
}

/** Lo ya hecho, para poder mostrar avance y no solo carencia. */
export function completedSteps(missing: readonly BookingBlocker[]): VerificationStep[] {
  return VERIFICATION_STEPS.filter((s) => !missing.includes(s.blocker))
}
